"use client";

import { useEffect, useState, type RefObject } from "react";

type CinematicWebRuntimeOptions = Readonly<{
    headerScrolledAttribute: string;
    revealedAttribute: string;
    revealAttribute: string;
    runtimeReadyAttribute: string;
    sentinelRef: RefObject<HTMLElement | null>;
    templateCode: string;
}>;

/**
 * Runtime shared by the Alcatraz-inspired templates. The presentation stays
 * template-owned; this hook only supplies the compact-header and reveal state
 * in the correct owner window (including Builder iframes).
 */
export function useCinematicWebRuntime({
    headerScrolledAttribute,
    revealedAttribute,
    revealAttribute,
    runtimeReadyAttribute,
    sentinelRef,
    templateCode,
}: CinematicWebRuntimeOptions): HTMLElement | null {
    const [header, setHeader] = useState<HTMLElement | null>(null);

    useEffect(() => {
        const template = sentinelRef.current?.closest<HTMLElement>(
            `[data-web-template="${templateCode}"]`,
        ) ?? null;
        const nextHeader = template?.querySelector<HTMLElement>(
            '[data-web-region="header"]',
        ) ?? null;
        const ownerDocument = template?.ownerDocument;
        const view = ownerDocument?.defaultView;

        setHeader(nextHeader);

        if (!template || !nextHeader || !ownerDocument || !view) {
            return;
        }

        const reducedMotion = view.matchMedia(
            "(prefers-reduced-motion: reduce)",
        ).matches;
        const revealSelector = [
            '[data-web-builder-region="page"] > .site-builder-section',
        ].join(",");
        const registered = new Set<HTMLElement>();
        let animationFrame: number | null = null;

        const updateHeader = () => {
            animationFrame = null;
            const threshold = view.matchMedia("(max-width: 900px)").matches
                ? 54
                : 110;

            nextHeader.toggleAttribute(
                headerScrolledAttribute,
                view.scrollY > threshold,
            );
        };
        const requestHeaderUpdate = () => {
            if (animationFrame === null) {
                animationFrame = view.requestAnimationFrame(updateHeader);
            }
        };
        const revealObserver = !reducedMotion && "IntersectionObserver" in view
            ? new view.IntersectionObserver((entries, observer) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting) continue;

                    entry.target.setAttribute(revealedAttribute, "true");
                    observer.unobserve(entry.target);
                }
            }, {
                rootMargin: "0px 0px -8% 0px",
                threshold: 0.12,
            })
            : null;
        const registerReveal = (root: ParentNode) => {
            const candidates = [
                ...(root instanceof view.HTMLElement && root.matches(revealSelector)
                    ? [root]
                    : []),
                ...Array.from(root.querySelectorAll<HTMLElement>(revealSelector)),
            ];

            for (const [index, element] of candidates.entries()) {
                if (registered.has(element)) continue;

                registered.add(element);
                element.setAttribute(revealAttribute, "true");

                if (index === 0 || !revealObserver) {
                    element.setAttribute(revealedAttribute, "true");
                } else {
                    revealObserver.observe(element);
                }
            }
        };

        updateHeader();
        view.addEventListener("scroll", requestHeaderUpdate, { passive: true });

        if (template.dataset.webTemplateEditor === "true") {
            template.setAttribute(runtimeReadyAttribute, "true");
            registerReveal(template);

            return () => {
                view.removeEventListener("scroll", requestHeaderUpdate);
                if (animationFrame !== null) {
                    view.cancelAnimationFrame(animationFrame);
                }
                nextHeader.removeAttribute(headerScrolledAttribute);
                template.removeAttribute(runtimeReadyAttribute);
                for (const element of registered) {
                    element.removeAttribute(revealAttribute);
                    element.removeAttribute(revealedAttribute);
                }
            };
        }

        registerReveal(template);
        template.setAttribute(runtimeReadyAttribute, "true");

        const mutationObserver = new view.MutationObserver((entries) => {
            for (const entry of entries) {
                for (const node of entry.addedNodes) {
                    if (node instanceof view.HTMLElement) registerReveal(node);
                }
            }
        });

        mutationObserver.observe(template, { childList: true, subtree: true });

        return () => {
            view.removeEventListener("scroll", requestHeaderUpdate);
            revealObserver?.disconnect();
            mutationObserver.disconnect();
            if (animationFrame !== null) {
                view.cancelAnimationFrame(animationFrame);
            }
            nextHeader.removeAttribute(headerScrolledAttribute);
            template.removeAttribute(runtimeReadyAttribute);
            for (const element of registered) {
                element.removeAttribute(revealAttribute);
                element.removeAttribute(revealedAttribute);
            }
        };
    }, [
        headerScrolledAttribute,
        revealedAttribute,
        revealAttribute,
        runtimeReadyAttribute,
        sentinelRef,
        templateCode,
    ]);

    return header;
}
